import styles from "./Button.module.css";

const LoadingSpinner = () => (
  <span className={styles.spinner}>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden="true"
      role="presentation"
    >
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  </span>
);

/**
 * Variant → CSS class mapping (uses global classes from button.css + antd-overrides.css)
 */
const VARIANT_CLASSES = {
  primary: "btn btn-primary",
  secondary: "btn btn-secondary-hover",
  outline: "btn btn-secondary-hover",
  danger: "btn btn-cancel-hover",
  ghost: "btn btn-tertiary",
  text: "btn btn-tertiary",
  link: "",
  "cancel-mobile": "btn btn-mobile-cancel-hover",
  "cancel-secondary": "btn btn-secondary-cancel-hover",
  "tertiary-hover": "btn btn-tertiary-hover",
};

const SIZE_CLASSES = {
  sm: "btn-sm",
  md: "btn-md",
  lg: "btn-lg",
};

const Button = ({
  variant = "primary",
  size,
  loading = false,
  disabled = false,
  icon,
  htmlType = "button",
  block = false,
  className = "",
  style,
  onClick,
  children,
  id,
  "aria-label": ariaLabel,
  ...rest
}) => {
  const isLink = variant === "link";
  const variantClass = VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary;
  const sizeClass = size ? SIZE_CLASSES[size] || "" : "";

  const classNames = [
    isLink ? styles.link : variantClass,
    sizeClass,
    loading ? styles.loading : "",
    block ? styles.fullWidth : "",
    styles.button,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type={htmlType}
      className={classNames}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      id={id}
      style={style}
      {...rest}
    >
      {loading && <LoadingSpinner />}
      {!loading && icon && <span className={styles.iconWrapper}>{icon}</span>}
      {children}
    </button>
  );
};

/* ── Variant shortcuts ── */

const PrimaryButton = props => (
  <Button
    variant="primary"
    {...props}
  />
);

const SecondaryButton = props => (
  <Button
    variant="secondary"
    {...props}
  />
);

const OutlineButton = props => (
  <Button
    variant="outline"
    {...props}
  />
);

const DangerButton = props => (
  <Button
    variant="danger"
    {...props}
  />
);

const TextButton = props => (
  <Button
    variant="text"
    {...props}
  />
);

const LinkButton = props => (
  <Button
    variant="link"
    {...props}
  />
);

const GhostButton = props => (
  <Button
    variant="ghost"
    {...props}
  />
);

export default Button;
export {Button, PrimaryButton, SecondaryButton, OutlineButton, DangerButton, TextButton, LinkButton, GhostButton};
